import { useEffect, useState } from 'react';
import { Modal, ConfirmModal } from './Modal.jsx';
import { Alert, Input, Loading, NumberInput, Textarea } from './ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { today } from '../lib/format.js';

const HINTS = {
  customer: t.openingBalance.hintCustomer,
  supplier: t.openingBalance.hintSupplier,
  exchange: t.openingBalance.hintExchange,
};

/**
 * One row per party, entered once when bringing in balances from before this
 * system existed. Reused across every party type - the sign-convention hint
 * is the only thing that changes between them.
 */
export function OpeningBalanceForm({ partyType, partyId, partyName, onClose, onSaved }) {
  const existing = useApi(`/api/opening-balances/${partyType}/${partyId}`);
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({ as_of_date: today(), iqd: '', usd: '', note: '' });
  const [confirmClear, setConfirmClear] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!existing.loading && !ready) {
      if (existing.data) {
        set({
          as_of_date: existing.data.as_of_date,
          iqd: existing.data.iqd,
          usd: existing.data.usd,
          note: existing.data.note || '',
        });
      }
      setReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.loading, existing.data]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/opening-balances/${partyType}/${partyId}`, form);
      toast.success(t.openingBalance.saved);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.del(`/api/opening-balances/${partyType}/${partyId}`);
      toast.success(t.openingBalance.cleared);
      setConfirmClear(false);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`${t.openingBalance.title} — ${partyName}`}
      onClose={onClose}
      footer={
        <>
          {existing.data && (
            <button type="button" className="btn btn-danger" onClick={() => setConfirmClear(true)} disabled={busy}>
              {t.openingBalance.clear}
            </button>
          )}
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !ready || !form.as_of_date}>
            {busy ? <span className="spinner" /> : t.openingBalance.save}
          </button>
        </>
      }
    >
      {!ready ? (
        <Loading />
      ) : (
        <>
          {error && <Alert tone="error">{error}</Alert>}
          <Alert tone="info">{HINTS[partyType]}</Alert>
          <Input
            label={t.openingBalance.date}
            required
            type="date"
            value={form.as_of_date}
            onChange={(e) => set({ as_of_date: e.target.value })}
            hint={t.openingBalance.dateHint}
          />
          <div className="form-grid cols-2">
            <NumberInput label={t.openingBalance.iqd} value={form.iqd} onChange={(iqd) => set({ iqd })} allowNegative />
            <NumberInput label={t.openingBalance.usd} value={form.usd} onChange={(usd) => set({ usd })} allowNegative />
          </div>
          <Textarea label={t.openingBalance.note} value={form.note} onChange={(e) => set({ note: e.target.value })} rows={2} />
        </>
      )}

      {confirmClear && (
        <ConfirmModal
          title={t.openingBalance.clear}
          message={t.openingBalance.clearConfirm}
          onConfirm={clear}
          onClose={() => setConfirmClear(false)}
          busy={busy}
        />
      )}
    </Modal>
  );
}
